import { Button } from '@cloudflare/kumo';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DangerButton } from '../ui/DangerButton';
import { ICONS, glyph } from '../ui/icons';
import { JevText } from './JevText';
import type { JevChat, Risk, Step } from './useJevChat';
import '../../styles/jev.css';

const TOOL_LABEL: Record<string, string> = {
  get_current_page_context: 'lendo a tela atual', search_workspace: 'buscando na organização', search_contacts: 'consultando contatos',
  get_contact: 'abrindo o contato', list_interactive_campaigns: 'consultando campanhas interativas', list_segments: 'consultando segmentos',
  get_segment: 'abrindo o segmento', list_sequences: 'consultando sequências', get_sequence: 'abrindo a sequência', query_metrics: 'calculando métricas',
  search_knowledge: 'consultando a base de conhecimento', create_contact: 'criando contato', update_contact: 'alterando contato', tag_contacts: 'aplicando tags',
  create_segment: 'criando segmento', update_segment_rules: 'alterando segmento',
  enroll_in_sequence: 'inscrevendo na sequência', send_message: 'enviando mensagem',
};
const RISK_LABEL: Record<Risk, string> = { read: 'consulta', write: 'alteração', sensitive: 'ação sensível', external: 'ação externa' };

function Timeline({ steps }: { steps: Step[] }) {
  if (!steps.length) return null;
  return (
    <ul className="jev-steps" aria-label="Ações da Lumi">
      {steps.map((s, i) => (
        <li key={i} className={`jev-step ${s.ok ? '' : 'jev-step--fail'}`}>
          {glyph(s.awaiting ? 'Clock' : s.ok ? 'Checkmark' : s.blocked ? 'Lock' : 'Warning', 16)}
          <span>{TOOL_LABEL[s.tool] ?? s.tool}</span>
          {s.risk !== 'read' && <span className="jev-step__risk">{RISK_LABEL[s.risk]}</span>}
          {s.awaiting && <span className="jev-step__note">aguardando confirmação</span>}
          {!s.ok && (s.blocked || s.error) && <span className="jev-step__note">{s.blocked || s.error}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Mensagens, passos das ferramentas e confirmações de uma conversa com a Lumi. */
export function JevConversation({ chat, onNavigate, avatars = false }: { chat: JevChat; onNavigate?: () => void; avatars?: boolean }) {
  const navigate = useNavigate();
  const endRef = useRef<HTMLDivElement>(null);
  const { items, pending, busy, deciding, decide } = chat;
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [items, busy, pending]);
  const go = (href: string) => { navigate(href); onNavigate?.(); };

  return (
    <>
      {items.map((it, i) => (
        it.kind === 'user' ? (
          <div key={i} className={avatars ? 'jev-row jev-row--user' : undefined}>
            <div className="jev-msg jev-msg--user">{it.text}</div>
            {avatars && <span className="jev-avatar jev-avatar--user" aria-hidden><ICONS.Person size={16} /></span>}
          </div>
        )
          : it.kind === 'error' ? <div key={i} className="jev-msg jev-msg--error" role="alert"><ICONS.Warning size={16} /> {it.text}</div>
            : (
              <div key={i} className={avatars ? 'jev-row' : undefined}>
                {avatars && <span className="jev-avatar jev-avatar--jev" aria-hidden><ICONS.Sparkle size={16} /></span>}
              <div className="jev-msg jev-msg--jev">
                <Timeline steps={it.steps} />
                <JevText text={it.text} />
                {it.affected.length > 0 && (
                  <div className="jev-links">
                    {it.affected.slice(0, 8).map((a) => (
                      <button key={`${a.type}:${a.id}`} type="button" onClick={() => go(a.href)} title={`Abrir ${a.label}`}>{a.label} <ICONS.ArrowRight size={16} /></button>
                    ))}
                    {it.affected.length > 8 && <span className="jev-muted">+{it.affected.length - 8}</span>}
                  </div>
                )}
              </div>
              </div>
            )
      ))}
      {pending.map((c) => (
        <div key={c.id} className={`jev-confirm jev-confirm--${c.risk}`} role="group" aria-label="Confirmação necessária">
          <div className="jev-confirm__head"><ICONS.Warning size={16} /> <div className="font-semibold">Confirmar {RISK_LABEL[c.risk]}</div></div>
          <div>{c.summary}</div>
          <div className="jev-muted">Nada é feito até você confirmar. Expira às {new Date(c.expiresAt).toLocaleTimeString('pt-BR', { timeStyle: 'short' })}.</div>
          <div className="jev-confirm__actions">
            <Button variant="secondary" onClick={() => decide(c, false)} disabled={!!deciding}>Cancelar</Button>
            {c.risk === 'write'
              ? <Button variant="primary" loading={deciding === c.id} disabled={!!deciding} onClick={() => decide(c, true)}>Confirmar</Button>
              : <DangerButton loading={deciding === c.id} disabled={!!deciding} onClick={() => decide(c, true)}>Confirmar</DangerButton>}
          </div>
        </div>
      ))}
      {busy && <div className="jev-msg jev-msg--jev jev-thinking" role="status"><span /><span /><span /> analisando…</div>}
      <div ref={endRef} />
    </>
  );
}
